/**
 * Error Reporting Utility
 * Automatically captures and reports errors to the admin console
 */

import { http } from '../dry/client';
import { CreateSystemErrorRequest } from '../types/admin';

// Browser detection utilities
function getBrowserInfo(): { name: string; version: string } {
  const ua = navigator.userAgent;
  let name = 'Unknown';
  let version = 'Unknown';

  if (ua.includes('Firefox/')) {
    name = 'Firefox';
    version = ua.split('Firefox/')[1]?.split(' ')[0] || 'Unknown';
  } else if (ua.includes('Edg/')) {
    name = 'Edge';
    version = ua.split('Edg/')[1]?.split(' ')[0] || 'Unknown';
  } else if (ua.includes('Chrome/')) {
    name = 'Chrome';
    version = ua.split('Chrome/')[1]?.split(' ')[0] || 'Unknown';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    name = 'Safari';
    version = ua.split('Version/')[1]?.split(' ')[0] || 'Unknown';
  }

  return { name, version };
}

function getOSInfo(): { name: string; version: string } {
  const ua = navigator.userAgent;
  let name = 'Unknown';
  let version = 'Unknown';

  if (ua.includes('Windows NT')) {
    name = 'Windows';
    const match = ua.match(/Windows NT (\d+\.\d+)/);
    if (match) {
      const ntVersion = match[1];
      // Map NT versions to Windows versions
      const versionMap: Record<string, string> = {
        '10.0': '10/11',
        '6.3': '8.1',
        '6.2': '8',
        '6.1': '7',
      };
      version = versionMap[ntVersion] || ntVersion;
    }
  } else if (ua.includes('Mac OS X')) {
    name = 'macOS';
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    version = match ? match[1].replace('_', '.') : 'Unknown';
  } else if (ua.includes('Linux')) {
    name = 'Linux';
  } else if (ua.includes('Android')) {
    name = 'Android';
    const match = ua.match(/Android (\d+)/);
    version = match ? match[1] : 'Unknown';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    name = 'iOS';
    const match = ua.match(/OS (\d+)/);
    version = match ? match[1] : 'Unknown';
  }

  return { name, version };
}

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

// Get current user and tenant info from localStorage
function getUserContext(): { userId?: string; tenantId?: string } {
  try {
    const userStr = localStorage.getItem('user');
    const tenantStr = localStorage.getItem('tenant');

    const user = userStr ? JSON.parse(userStr) : null;
    const tenant = tenantStr ? JSON.parse(tenantStr) : null;

    return {
      userId: user?.id,
      tenantId: tenant?.id,
    };
  } catch {
    return {};
  }
}

// Debounce mechanism to avoid flooding the API
const reportedErrors = new Map<string, number>();
const DEBOUNCE_INTERVAL = 60000; // 1 minute

function shouldReportError(errorKey: string): boolean {
  const lastReported = reportedErrors.get(errorKey);
  const now = Date.now();

  if (lastReported && now - lastReported < DEBOUNCE_INTERVAL) {
    return false;
  }

  reportedErrors.set(errorKey, now);

  // Clean up old entries
  if (reportedErrors.size > 100) {
    const cutoff = now - DEBOUNCE_INTERVAL;
    for (const [key, time] of reportedErrors.entries()) {
      if (time < cutoff) {
        reportedErrors.delete(key);
      }
    }
  }

  return true;
}

// Generate error key for deduplication
function getErrorKey(message: string, stack?: string): string {
  const stackLine = stack?.split('\n')[1] || '';
  return `${message}:${stackLine}`.substring(0, 200);
}

// Determine error severity based on error type and message
function determineSeverity(error: Error | string, errorType: string): 'debug' | 'info' | 'warning' | 'error' | 'fatal' {
  const message = typeof error === 'string' ? error : error.message;
  const lowerMessage = message.toLowerCase();

  // Fatal errors
  if (lowerMessage.includes('fatal') || lowerMessage.includes('crash') || errorType === 'frontend') {
    return 'error'; // Component crashes are serious but not fatal
  }

  // Network errors are usually warnings (user might be offline)
  if (lowerMessage.includes('network') || lowerMessage.includes('fetch') || lowerMessage.includes('timeout')) {
    return 'warning';
  }

  // Authentication errors
  if (lowerMessage.includes('401') || lowerMessage.includes('unauthorized')) {
    return 'warning';
  }

  // Validation errors are info level
  if (lowerMessage.includes('validation') || lowerMessage.includes('invalid')) {
    return 'info';
  }

  // Default to error level
  return 'error';
}

/**
 * Report an error to the admin console
 */
export async function reportError(
  error: Error | string,
  options: {
    errorType?: 'frontend' | 'backend' | 'api' | 'websocket';
    severity?: 'debug' | 'info' | 'warning' | 'error' | 'fatal';
    componentName?: string;
    componentStack?: string;
    metadata?: Record<string, unknown>;
    tags?: string[];
  } = {}
): Promise<void> {
  const {
    errorType = 'frontend',
    componentName,
    componentStack,
    metadata = {},
    tags = [],
  } = options;

  const message = typeof error === 'string' ? error : error.message;
  const stack = typeof error === 'string' ? undefined : error.stack;

  // Generate error key for deduplication
  const errorKey = getErrorKey(message, stack);

  // Check if we should report this error (debouncing)
  if (!shouldReportError(errorKey)) {
    console.debug('[ErrorReporting] Skipping duplicate error:', message);
    return;
  }

  const severity = options.severity || determineSeverity(error, errorType);
  const browser = getBrowserInfo();
  const os = getOSInfo();
  const deviceType = getDeviceType();
  const { userId, tenantId } = getUserContext();

  const request: CreateSystemErrorRequest = {
    error_type: errorType,
    severity,
    message: message.substring(0, 5000), // Limit message length
    stack_trace: componentStack
      ? `${stack || ''}\n\nComponent Stack:\n${componentStack}`.substring(0, 10000)
      : stack?.substring(0, 10000),

    // URL and context
    url: window.location.href,
    user_agent: navigator.userAgent,

    // Browser info
    browser_name: browser.name,
    browser_version: browser.version,
    os_name: os.name,
    os_version: os.version,
    device_type: deviceType,

    // Application context
    app_version: import.meta.env.REACT_APP_VERSION || '1.0.0',
    environment: import.meta.env.MODE,
    component_name: componentName,

    // User context (optional)
    user_id: userId,
    tenant_id: tenantId,

    // Additional data
    metadata: {
      ...metadata,
      timestamp: new Date().toISOString(),
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      language: navigator.language,
      online: navigator.onLine,
    },
    tags: [...tags, errorType, severity],
  };

  try {
    // Don't await to avoid blocking the UI
    http.post<any>('/admin/errors', request).catch((apiError: any) => {
      // Log to console if API fails (don't recursively report)
      console.error('[ErrorReporting] Failed to report error:', apiError);
    });

    console.debug('[ErrorReporting] Error reported:', message);
  } catch (e) {
    console.error('[ErrorReporting] Exception while reporting:', e);
  }
}

/**
 * Report an API error (called from axios interceptor)
 */
export function reportApiError(
  url: string,
  method: string,
  status: number,
  errorMessage: string,
  responseData?: unknown
): void {
  const message = `API Error: ${method.toUpperCase()} ${url} - ${status} ${errorMessage}`;

  reportError(message, {
    errorType: 'api',
    severity: status >= 500 ? 'error' : 'warning',
    metadata: {
      request_method: method,
      request_path: url,
      response_status: status,
      response_data: responseData,
    },
    tags: ['api', `status-${status}`, method.toLowerCase()],
  });
}

/**
 * Report a component error (called from ErrorBoundary)
 */
export function reportComponentError(
  error: Error,
  componentStack: string,
  componentName?: string
): void {
  reportError(error, {
    errorType: 'frontend',
    severity: 'error',
    componentName: componentName || 'Unknown',
    componentStack,
    tags: ['react', 'component-crash'],
  });
}

/**
 * Report an unhandled promise rejection
 */
export function reportUnhandledRejection(reason: unknown): void {
  const error = reason instanceof Error
    ? reason
    : new Error(String(reason));

  reportError(error, {
    errorType: 'frontend',
    severity: 'warning',
    tags: ['unhandled-rejection', 'promise'],
  });
}

/**
 * Report a global JavaScript error
 */
export function reportGlobalError(
  message: string,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
): void {
  const fullMessage = error?.message || message;
  const stack = error?.stack || `at ${source}:${lineno}:${colno}`;

  reportError(new Error(fullMessage), {
    errorType: 'frontend',
    severity: 'error',
    metadata: {
      source,
      line: lineno,
      column: colno,
    },
    tags: ['global-error', 'window-onerror'],
  });
}

// Detects "old bundle is referencing a chunk that no longer exists on the
// server" — happens after a redeploy when the user's tab still has the
// previous index-*.js loaded. Both Chrome and Firefox surface this through
// the unhandled-rejection channel when a React.lazy() import fails.
function isStaleChunkError(reason: unknown): boolean {
  const msg = (reason instanceof Error ? reason.message : String(reason || '')).toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes('importing a module script failed') ||
    msg.includes('chunkloaderror')
  );
}

const STALE_RELOAD_FLAG = 'cnt_stale_reload_at';

function recoverFromStaleChunk(): boolean {
  // Guard against an infinite reload loop: only auto-reload once per minute.
  const now = Date.now();
  const last = Number(sessionStorage.getItem(STALE_RELOAD_FLAG) || '0');
  if (now - last < 60_000) return false;
  sessionStorage.setItem(STALE_RELOAD_FLAG, String(now));
  console.warn('[ErrorReporting] Stale bundle detected — reloading to fetch fresh assets');
  // Bypass HTTP cache for the navigation
  window.location.reload();
  return true;
}

/**
 * Initialize global error handlers
 * Call this once at application startup
 */
export function initializeErrorReporting(): void {
  // Only initialize in browser environment
  if (typeof window === 'undefined') {
    return;
  }

  // Global error handler
  window.onerror = (message, source, lineno, colno, error) => {
    if (isStaleChunkError(error || message)) {
      if (recoverFromStaleChunk()) return true;
    }
    reportGlobalError(
      typeof message === 'string' ? message : 'Unknown error',
      source,
      lineno,
      colno,
      error
    );
    // Don't prevent default error handling
    return false;
  };

  // Unhandled promise rejection handler
  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    if (isStaleChunkError(event.reason)) {
      event.preventDefault();
      recoverFromStaleChunk();
      return;
    }
    reportUnhandledRejection(event.reason);
    // Don't prevent default handling
  };

  console.debug('[ErrorReporting] Global error handlers initialized');
}

export default {
  reportError,
  reportApiError,
  reportComponentError,
  reportUnhandledRejection,
  reportGlobalError,
  initializeErrorReporting,
};
